import Link from 'next/link';
import React, { useState } from 'react';
import { useRouter } from 'next/router';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import {
  correctInternalLink,
  isGrayScaleOrColor,
  isItUndefined,
  isLinkAbsolute,
  modifyLinks,
} from '../../util/DataUtil';
import {
  brandColor,
  getClassTextForColor,
  getEditorColor,
} from '../../util/ColorUtil';
import { Constants } from '../../util/Constants';
import { cleanHTML } from '../utils/htmlUtils';

const Awards = ({ data, lang }) => {
  const router = useRouter();
  const emphasizedTextColor = getEditorColor(
    isItUndefined(data?.field_title_color)
  );
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [filter, setFilter] = useState('Global');

  if (!data?.field_award_items) return null;

  const accordionTitle = data?.field_accordion_title?.[0]?.value || '';

  const allCertifications =
    data.field_award_items.image_text_link_paragraph.filter(
      (cert) => cert?.field_certification_status?.[0]?.value === 'Active'
    ) || [];

  let accordionSection = [];
  let featuredSection = [];
  let hideFeaturedSection =
    data.field_hide_featured_section?.[0]?.value === '1';

  if (hideFeaturedSection) {
    accordionSection = allCertifications;
  } else {
    const featuredCerts = allCertifications.filter(
      (cert) => cert?.field_is_certificate_featured?.[0]?.value === '1'
    );
    const nonFeaturedCerts = allCertifications.filter(
      (cert) => cert?.field_is_certificate_featured?.[0]?.value === '0'
    );

    if (featuredCerts.length < 3) {
      accordionSection = allCertifications; //[...featuredCerts, ...nonFeaturedCerts]
      hideFeaturedSection = true;
    } else {
      const featuredCertsLocal = featuredCerts.filter(
        (cert) => cert?.field_certification_type?.[0]?.value === 'Local'
      );
      const featuredCertsGlobal = featuredCerts.filter(
        (cert) => cert?.field_certification_type?.[0]?.value === 'Global'
      );

      const featuredCertsSorted = [
        ...featuredCertsLocal,
        ...featuredCertsGlobal,
      ];

      featuredSection = featuredCertsSorted.slice(0, 6);
      accordionSection = [...featuredCertsSorted.slice(6), ...nonFeaturedCerts];
    }

    //mozda slucaj ako je featuredCerts.length<3 move all to accordion, 3<featuredCerts.length<6 slice 3 rest accordion, else slice 6
  }

  const localAccordionCerts =
    lang !== 'en'
      ? accordionSection.filter(
          (cert) => cert?.field_certification_type?.[0]?.value === 'Local'
        )
      : [];
  const globalAccordionCerts = accordionSection.filter(
    (cert) => cert?.field_certification_type?.[0]?.value === 'Global'
  );

  if (
    globalAccordionCerts.length === 0 &&
    localAccordionCerts.length !== 0 &&
    filter !== 'Local'
  ) {
    //freeze toggle if there is only local non-featured certs, probably never the case
    setFilter('Local');
  }

  //If new fields are still empty in CMS, display as it was before - all certs in featured section
  if (
    !data.field_award_items.image_text_link_paragraph.some(
      (cert) => cert?.field_certification_type?.[0]?.value === 'Global'
    ) &&
    !data.field_award_items.image_text_link_paragraph.some(
      (cert) => cert?.field_certification_type?.[0]?.value === 'Local'
    )
  ) {
    featuredSection = data.field_award_items.image_text_link_paragraph;
    hideFeaturedSection = false;
    accordionSection = [];
  }

  const renderItems = (items) => {
    return items.map((item, index) => {
      const paragraphLink = item?.field_paragraph_link?.[0];
      const paragraphImage = item?.field_paragraph_image?.[0];
      const titleValue = item?.field_title?.[0]?.value;

      return (
        <div
          key={`awards-key-${index}`}
          className={'item'}
          onClick={() => {
            if (isLinkAbsolute(paragraphLink?.uri)) {
              window.open(paragraphLink?.uri, '_blank');
            } else {
              router.push(paragraphLink?.uri);
            }
          }}
        >
          <img
            alt={'image'}
            className={'item-image'}
            src={Constants.cloudinaryImage + paragraphImage}
          />
          <span
            className={`title ${emphasizedTextColor || ''}`}
            dangerouslySetInnerHTML={{ __html: modifyLinks(titleValue) }}
          />
          {correctInternalLink(paragraphLink?.uri) && (
            <Link
              href={correctInternalLink(paragraphLink?.uri)}
              passHref
              legacyBehavior
            >
              <a
                target={isLinkAbsolute(paragraphLink?.uri) ? '_blank' : '_self'}
                className="a-awards-link cta-link"
              >
                {paragraphLink?.title}
                <img
                  alt="arrow"
                  className="more-arrow"
                  src="/images/more-arrow.svg"
                />
              </a>
            </Link>
          )}
        </div>
      );
    });
  };

  const renderAccordionItems = (items) => {
    if (!items.length) return null;

    const filteredItems =
      filter === 'Global' ? globalAccordionCerts : localAccordionCerts;

    const visibleItems = showMore ? filteredItems : filteredItems.slice(0, 5);

    return (
      <div className="inner-container">
        {localAccordionCerts.length > 0 ? (
          <div
            className="filter-buttons"
            style={{
              marginTop:
                featuredSection.length > 6 ||
                (featuredSection.length > 0 && accordionSection.length > 0)
                  ? '170px'
                  : '0',
            }}
          >
            <button
              className={
                filter === 'Global' ? 'active button-left' : 'button-left'
              }
              onClick={() => {
                setIsAccordionOpen(null);
                setFilter('Global');
              }}
            >
              Global
            </button>
            <span className="separator"></span>
            <button
              className={
                filter === 'Local' ? 'active button-right' : 'button-right'
              }
              onClick={() => {
                setIsAccordionOpen(null);
                setFilter('Local');
              }}
            >
              Local
            </button>
          </div>
        ) : null}
        <p className="accordion-top-title">{accordionTitle}</p>
        {visibleItems.map((item, index) => (
          <Accordion
            key={`accordion-item-${index}`}
            expanded={isAccordionOpen === index}
            onChange={() =>
              setIsAccordionOpen((prev) => (prev === index ? null : index))
            }
            sx={{
              backgroundColor: '#F2F2F2',
              boxShadow: 'none',
              border: 'none',
              margin: '0',
              borderBottom: '0.5px solid black',
            }}
            className="andrija"
          >
            <AccordionSummary
              expandIcon={
                <img
                  src="/images/accordion-arrow.svg"
                  alt="arrow"
                  style={{ width: '32px', height: '32px' }}
                />
              }
              sx={{ color: 'black' }}
            >
              <Typography className="accordion-title" sx={{ color: 'black' }}>
                <img src="/images/blue_checkmark.svg" alt="checkmark" />
                {item?.field_title?.[0]?.value}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div
                className="accordion-description"
                dangerouslySetInnerHTML={{
                  __html: item?.field_certificate_description?.[0]?.value,
                }}
              />
            </AccordionDetails>
          </Accordion>
        ))}
        {filteredItems.length > 5 ? (
          <button
            className="accordion-see-more"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? 'See less' : 'See more'}
          </button>
        ) : null}
      </div>
    );
  };

  const fullSpanValue = data?.field_full_span?.[0]?.value;
  const backgroundColorValue = data?.field_background_color?.[0]?.value;

  return (
    <div
      className={`awards ${isGrayScaleOrColor(data?.field_hover_type?.[0]?.value)} ${getClassTextForColor(backgroundColorValue)}`}
      style={{
        backgroundColor:
          fullSpanValue === '1' ? brandColor(backgroundColorValue) : '#F2F2F2',
      }}
    >
      {fullSpanValue === '1' ? (
        <div>
          {' '}
          {data?.field_title_color?.[0]?.value ? (
            <h2
              className={`awards-heading-full ${getClassTextForColor(backgroundColorValue)}`}
              dangerouslySetInnerHTML={{
                __html:
                  cleanHTML(modifyLinks(data?.field_title_color?.[0]?.value)) || //field_title_color is machine name for title field
                  '',
              }}
            />
          ) : null}
          <div className="items">
            {!hideFeaturedSection ? renderItems(featuredSection) : null}
            {localAccordionCerts.length > 0 ||
            globalAccordionCerts.length > 0 ? (
              <div
                style={{
                  backgroundColor: '#F2F2F2',
                  width: '100%',
                }}
              >
                {renderAccordionItems([...accordionSection])}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="inner-container">
          <h2
            // className={emphasizedTextColor || ''}
            className={'awards-heading'}
            dangerouslySetInnerHTML={{
              __html:
                cleanHTML(modifyLinks(data?.field_title_color?.[0]?.value)) || //field_title_color is machine name for title field
                '',
            }}
          />

          {!hideFeaturedSection ? (
            <div
              className="items"
              style={{
                backgroundColor:
                  fullSpanValue === '0' ? brandColor(backgroundColorValue) : '',
              }}
            >
              {renderItems(featuredSection)}
            </div>
          ) : null}

          {localAccordionCerts.length > 0 || globalAccordionCerts.length > 0
            ? renderAccordionItems([...accordionSection])
            : null}
        </div>
      )}

      {/*<div*/}
      {/*    className="items"*/}
      {/*    style={{*/}
      {/*        backgroundColor:*/}
      {/*            fullSpanValue === '1' ? brandColor(backgroundColorValue) : '',*/}
      {/*    }}*/}
      {/*>*/}
      {/*    /!*{renderItems(featuredCerts.slice(0, 6))}*!/*/}
      {/*    <div className="items">*/}
      {/*        {renderItems(data?.field_award_items?.image_text_link_paragraph)}*/}
      {/*    </div>*/}

      {/*</div>*/}
      {/*{renderAccordionItems([*/}
      {/*   ...featuredCerts.slice(6),*/}
      {/*   ...nonFeaturedCerts,*/}
      {/*])}*/}

      {/*{lang !== 'fr' ? (*/}
      {/*  fullSpanValue === '1' ? (*/}
      {/*    <div className="items">*/}
      {/*      {renderItems(data?.field_award_items?.image_text_link_paragraph)}*/}
      {/*    </div>*/}
      {/*  ) : (*/}
      {/*    <div className="inner-container">*/}
      {/*      <h2*/}
      {/*        className={emphasizedTextColor || ''}*/}
      {/*        dangerouslySetInnerHTML={{*/}
      {/*          __html:*/}
      {/*            cleanHTML(modifyLinks(data?.field_title_color?.[0]?.value)) ||*/}
      {/*            '',*/}
      {/*        }}*/}
      {/*      />*/}
      {/*      <div*/}
      {/*        className="items"*/}
      {/*        style={{*/}
      {/*          backgroundColor:*/}
      {/*            fullSpanValue === '0' ? brandColor(backgroundColorValue) : '',*/}
      {/*        }}*/}
      {/*      >*/}
      {/*        {renderItems(data?.field_award_items?.image_text_link_paragraph)}*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*  )*/}
      {/*) : featuredCerts.length === 0 ? (*/}
      {/*  renderAccordionItems([...nonFeaturedCerts])*/}
      {/*) : featuredCerts.length >= 3 && featuredCerts.length <= 6 ? (*/}
      {/*  <div*/}
      {/*    className="items"*/}
      {/*    style={{*/}
      {/*      backgroundColor:*/}
      {/*        fullSpanValue === '1' ? brandColor(backgroundColorValue) : '',*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    {renderItems(featuredCerts)}*/}
      {/*  </div>*/}
      {/*) : (*/}
      {/*  <>*/}
      {/*    <div*/}
      {/*      className="items"*/}
      {/*      style={{*/}
      {/*        backgroundColor:*/}
      {/*          fullSpanValue === '1' ? brandColor(backgroundColorValue) : '',*/}
      {/*      }}*/}
      {/*    >*/}
      {/*      {renderItems(featuredCerts.slice(0, 6))}*/}
      {/*    </div>*/}
      {/*    {renderAccordionItems([*/}
      {/*      ...featuredCerts.slice(6),*/}
      {/*      ...nonFeaturedCerts,*/}
      {/*    ])}*/}
      {/*  </>*/}
      {/*)}*/}
    </div>
  );
};

export default React.memo(Awards);
